import React from "react";
import OfferPageInfo from "@/components/offer/OfferPageInfo";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface NoProductsWarningProps {
  offerId: string;
  productCount: number;
  rawProductIds?: string[];
}

const NoProductsWarning: React.FC<NoProductsWarningProps> = ({
  offerId,
  productCount,
  rawProductIds,
}) => {
  const navigate = useNavigate();
  return (
    <div className=" text-center mt-20 mb-4 p-6 bg-amber-50 rounded-lg border border-amber-200">
      <div className="flex items-center justify-center gap-2 mb-4">
        <AlertTriangle className="h-6 w-6 text-amber-600" />
        <h2 className="text-lg font-semibold text-amber-700">
          Create Your First Bundle
        </h2>
      </div>

      <p className="text-amber-700 mb-4">
        You need to create some active products or subscriptions before you can
        create an bundle page.
      </p>

      <OfferPageInfo offerId={offerId} productCount={productCount} />

      <div className="mt-6 space-y-4">
        <Alert className="bg-blue-50 border-blue-200">
          <AlertDescription className="text-blue-700">
            Let's get started by creating your first product. Then you can combine them into a bundle:
          </AlertDescription>
        </Alert>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            onClick={() => navigate("/dashboard?tab=products&action=create")}
          >
            <Plus className="h-4 w-4" />
            Create Product
          </Button>
        </div>
        
        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Once you have products, you can create your first bundle to offer them together.
          </p>
        </div>
      </div>

      {rawProductIds && rawProductIds.length > 0 && (
        <div className="mt-4 p-4 bg-white rounded border border-amber-100 text-left">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-amber-500 mt-1 mr-2 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-800 font-medium mb-2">
                Products attached but inactive or unavailable:
              </p>
              <ul className="text-xs text-amber-700 mt-1 list-disc pl-5 mb-3">
                {rawProductIds.map((id, index) => (
                  <li key={index}>{id}</li>
                ))}
              </ul>
              <p className="text-xs text-amber-700 mb-3">
                The products above might be inactive, deleted, or improperly
                configured. To fix this issue:
              </p>
              <ol className="text-xs text-amber-700 list-decimal pl-5 mb-3">
                <li>
                  Go to the Products tab and check if these products exist and
                  are marked as "Active"
                </li>
                <li>
                  If they don't exist, edit your Bundle Page to include active
                  products
                </li>
                <li>
                  If they exist but are inactive, activate them or choose
                  different products
                </li>
              </ol>
              <div className="flex justify-end">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-xs"
                  onClick={() => navigate("/dashboard?tab=products")}
                >
                  View Products
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoProductsWarning;
